/**
 * SceneCanvas — image-contained coordinate system with zoom/pan and hitboxes
 * that scale with the image. All hitboxes live inside the transform container
 * so tap coordinates map correctly at any zoom level.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { CheckCircle2 } from "lucide-react";
import { GlowOverlay, SparkleOverlay, WarmColdOverlay } from "./hints/HintOverlays";
import {
  isPointInHitbox,
  effectivePaddedRect,
  effectivePaddedPolygon,
  effectivePaddedCircle,
} from "./hitboxMath";

// Palette for the debug overlay so each object's effective tap area gets a
// distinct colour + label. Alpha is baked into the fill so the underlying
// scene stays visible.
const DEBUG_FILL = [
  "rgba(239, 68, 68, 0.35)", "rgba(59, 130, 246, 0.35)", "rgba(34, 197, 94, 0.35)",
  "rgba(245, 158, 11, 0.35)", "rgba(168, 85, 247, 0.35)", "rgba(20, 184, 166, 0.35)",
  "rgba(236, 72, 153, 0.35)", "rgba(132, 204, 22, 0.35)", "rgba(249, 115, 22, 0.35)",
  "rgba(6, 182, 212, 0.35)",
];
const DEBUG_STROKE = [
  "#ef4444", "#3b82f6", "#22c55e", "#f59e0b", "#a855f7",
  "#14b8a6", "#ec4899", "#84cc16", "#f97316", "#06b6d4",
];

function computeRenderRect(container, natural) {
  // container: { w, h }; natural: { w, h }
  if (!container.w || !container.h || !natural.w || !natural.h) {
    return { x: 0, y: 0, w: container.w, h: container.h };
  }
  const containerAspect = container.w / container.h;
  const imgAspect = natural.w / natural.h;
  if (imgAspect > containerAspect) {
    // image is wider — letterboxed top/bottom
    const w = container.w;
    const h = w / imgAspect;
    return { x: 0, y: (container.h - h) / 2, w, h };
  }
  const h = container.h;
  const w = h * imgAspect;
  return { x: (container.w - w) / 2, y: 0, w, h };
}

export function SceneCanvas({
  sceneImageUrl,
  objects,
  foundIds,
  onHit,
  onMiss,
  showAllHitboxesDebug = false,
  glowId,
  narrationLock,
  transformRef,
  onZoomChanged,
  hintOverlay,
  onPointerMovePercent,
}) {
  const containerRef = useRef(null);
  const [container, setContainer] = useState({ w: 0, h: 0 });
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [status, setStatus] = useState("loading"); // "loading" | "ready" | "error"

  // Resize observation
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setContainer({ w: e.contentRect.width, h: e.contentRect.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Load the image + measure natural size
  useEffect(() => {
    if (!sceneImageUrl) return;
    setStatus("loading");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
      setStatus("ready");
    };
    img.onerror = () => setStatus("error");
    img.src = sceneImageUrl;
  }, [sceneImageUrl]);

  const renderRect = useMemo(
    () => computeRenderRect(container, natural),
    [container, natural],
  );

  // Per-object tap debounce (150ms). On touch, users often "double-tap" as
  // their finger settles — this collapses repeat taps on the SAME object
  // within the window into one credited hit.
  const lastTapRef = useRef({}); // { [objectId]: timestamp_ms }
  // Multi-touch guard: while a pinch/pan gesture is in progress, ignore
  // synthetic click events that fire on gesture end. TransformWrapper handles
  // the gesture math; we just need to swallow the tap that follows.
  const multiTouchRef = useRef(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const start = (e) => {
      if (e.touches && e.touches.length > 1) multiTouchRef.current = true;
    };
    const end = () => {
      // Small delay so the trailing click event still sees the guard set.
      setTimeout(() => { multiTouchRef.current = false; }, 250);
    };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchend", end, { passive: true });
    el.addEventListener("touchcancel", end, { passive: true });
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", end);
    };
  }, []);

  // A tap on the SceneCanvas stage — pointer coords are already relative to
  // the letterbox-cropped stage (which sits at image bounds), so we can go
  // straight to image percentages.
  const handleTap = useCallback(
    (event) => {
      if (narrationLock) return;
      if (status !== "ready") return;
      if (multiTouchRef.current) return; // swallow pinch/pan click
      const bounds = event.currentTarget.getBoundingClientRect();
      const localX = event.clientX - bounds.left;
      const localY = event.clientY - bounds.top;
      if (localX < 0 || localY < 0 || localX > bounds.width || localY > bounds.height) return;
      const xPct = (localX / bounds.width) * 100;
      const yPct = (localY / bounds.height) * 100;

      // Iterate objects sorted by area asc so smaller ones win overlaps.
      const sorted = [...(objects || [])].sort(
        (a, b) => a.widthPercent * a.heightPercent - b.widthPercent * b.heightPercent,
      );
      const now = Date.now();
      for (const o of sorted) {
        if (foundIds.has(o.id)) continue;
        if (isPointInHitbox(xPct, yPct, o)) {
          // 150ms per-object debounce
          const last = lastTapRef.current[o.id] || 0;
          if (now - last < 150) return;
          lastTapRef.current[o.id] = now;
          onHit(o, { xPercent: xPct, yPercent: yPct });
          return;
        }
      }
      onMiss({ xPercent: xPct, yPercent: yPct });
    },
    [foundIds, narrationLock, objects, onHit, onMiss, status],
  );

  // Focusable + keyboard-activatable hitboxes for accessibility
  const handleKey = (event, obj) => {
    if (narrationLock) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!foundIds.has(obj.id)) {
        onHit(obj, {
          xPercent: obj.xPercent + obj.widthPercent / 2,
          yPercent: obj.yPercent + obj.heightPercent / 2,
        });
      }
    }
  };

  return (
    <div className="scene-canvas" ref={containerRef} data-testid="scene-canvas">
      {status === "loading" && (
        <div className="scene-canvas__skeleton" data-testid="scene-loading">
          Getting the picture ready…
        </div>
      )}
      {status === "error" && (
        <div className="scene-canvas__error" data-testid="scene-error">
          <p>This picture needs another moment. Let&apos;s try again.</p>
        </div>
      )}
      {status === "ready" && (
        <TransformWrapper
          minScale={1}
          maxScale={3}
          initialScale={1}
          doubleClick={{ mode: "reset" }}
          onZoom={({ state }) => onZoomChanged?.(state.scale)}
          ref={transformRef}
        >
          <TransformComponent
            wrapperStyle={{ width: "100%", height: "100%" }}
            contentStyle={{ width: "100%", height: "100%" }}
          >
            <div
              className="scene-canvas__stage"
              style={{
                position: "relative",
                width: renderRect.w,
                height: renderRect.h,
                marginLeft: renderRect.x,
                marginTop: renderRect.y,
              }}
              onClick={handleTap}
              onMouseMove={(e) => {
                if (!onPointerMovePercent) return;
                const bounds = e.currentTarget.getBoundingClientRect();
                const x = ((e.clientX - bounds.left) / bounds.width) * 100;
                const y = ((e.clientY - bounds.top) / bounds.height) * 100;
                if (x >= 0 && x <= 100 && y >= 0 && y <= 100) onPointerMovePercent({ xPercent: x, yPercent: y });
              }}
              onTouchMove={(e) => {
                if (!onPointerMovePercent || !e.touches.length) return;
                const t = e.touches[0];
                const bounds = e.currentTarget.getBoundingClientRect();
                const x = ((t.clientX - bounds.left) / bounds.width) * 100;
                const y = ((t.clientY - bounds.top) / bounds.height) * 100;
                if (x >= 0 && x <= 100 && y >= 0 && y <= 100) onPointerMovePercent({ xPercent: x, yPercent: y });
              }}
              role="application"
              aria-label="Hidden object scene"
            >
              <img
                src={sceneImageUrl}
                alt="Scene"
                className="scene-canvas__img"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
                draggable={false}
              />
              {(objects || []).map((o, idx) => {
                const pad = o.hitboxPaddingPercent ?? 0;
                const padX = (o.widthPercent * pad) / 100;
                const padY = (o.heightPercent * pad) / 100;
                const leftPct = Math.max(0, o.xPercent - padX);
                const topPct = Math.max(0, o.yPercent - padY);
                const wPct = Math.min(100, o.widthPercent + padX * 2);
                const hPct = Math.min(100, o.heightPercent + padY * 2);
                const found = foundIds.has(o.id);
                const glow = glowId === o.id;
                return (
                  <div
                    key={o.id}
                    className={`scene-canvas__hit${found ? " scene-canvas__hit--found" : ""}${glow ? " scene-canvas__hit--glow" : ""}`}
                    role="button"
                    tabIndex={found ? -1 : 0}
                    aria-label={o.learningObject?.displayName || "Target"}
                    data-testid={`hit-${o.learningObject?.canonicalName || o.id}`}
                    onKeyDown={(e) => handleKey(e, o)}
                    // No onClick — stage-level handleTap owns pointer picking so
                    // shape-aware isPointInHitbox is enforced client-side. This
                    // eliminates the "flash of feedback then server-reject" case
                    // where a tap inside the padded rect but outside the polygon
                    // used to still fire onHit. Keyboard access is preserved via
                    // onKeyDown (Enter/Space → activate at shape center).
                    style={{
                      position: "absolute",
                      left: `${leftPct}%`,
                      top: `${topPct}%`,
                      width: `${wPct}%`,
                      height: `${hPct}%`,
                    }}
                  >
                    {found && (
                      <span className="scene-canvas__hit-label">
                        <CheckCircle2 size={22} color="#4c956c" />
                      </span>
                    )}
                  </div>
                );
              })}

              {/* Debug overlay — the EFFECTIVE PADDED shape (rect/polygon/circle)
                  drawn as SVG using the same math the tap-check uses. When
                  padding = 0 (current seed) this equals the raw object outline
                  so image-analysis of a screenshot verifies exactly what the
                  server credits. */}
              {showAllHitboxesDebug && (
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{
                    position: "absolute", left: 0, top: 0,
                    width: "100%", height: "100%",
                    pointerEvents: "none",
                  }}
                >
                  {(objects || []).map((o, idx) => {
                    const fill = DEBUG_FILL[idx % DEBUG_FILL.length];
                    const stroke = DEBUG_STROKE[idx % DEBUG_STROKE.length];
                    const shape = o.hitboxShape || "rectangle";
                    if (shape === "polygon") {
                      const pts = effectivePaddedPolygon(o);
                      if (!pts) return null;
                      return (
                        <polygon
                          key={`dbg-${o.id}`}
                          points={pts.map((p) => `${p[0]},${p[1]}`).join(" ")}
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={0.3}
                        />
                      );
                    }
                    if (shape === "circle") {
                      const c = effectivePaddedCircle(o);
                      if (!c) return null;
                      return (
                        <circle
                          key={`dbg-${o.id}`}
                          cx={c.cx}
                          cy={c.cy}
                          r={c.radius}
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={0.3}
                        />
                      );
                    }
                    const r = effectivePaddedRect(o);
                    return (
                      <rect
                        key={`dbg-${o.id}`}
                        x={r.left}
                        y={r.top}
                        width={r.right - r.left}
                        height={r.bottom - r.top}
                        fill={fill}
                        stroke={stroke}
                        strokeWidth={0.3}
                      />
                    );
                  })}
                </svg>
              )}
              {showAllHitboxesDebug && (objects || []).map((o) => (
                <div
                  key={`dbg-label-${o.id}`}
                  style={{
                    position: "absolute",
                    left: `${o.xPercent}%`,
                    top: `${o.yPercent}%`,
                    fontSize: 10,
                    color: "#fff",
                    fontWeight: 800,
                    textShadow: "0 1px 2px rgba(0,0,0,0.9)",
                    pointerEvents: "none",
                    padding: 2,
                  }}
                >
                  {o.learningObject?.canonicalName}
                </div>
              ))}
              {hintOverlay?.type === "object_glow" && (
                <GlowOverlay hitbox={hintOverlay.objectHitbox} />
              )}
              {hintOverlay?.type === "regional_sparkle" && (
                <SparkleOverlay region={hintOverlay.hintRegion} />
              )}
              {hintOverlay?.type === "warm_cold" && (
                <WarmColdOverlay pointer={hintOverlay.pointer} targetCenter={hintOverlay.targetCenter} />
              )}
              {showAllHitboxesDebug && (
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%", pointerEvents: "none" }}
                  data-testid="hitbox-debug-svg"
                >
                  {(objects || []).map((o) => {
                    const shape = o.hitboxShape || "rectangle";
                    if (shape === "circle" && o.hitboxCircle) {
                      return (
                        <circle key={o.id} cx={o.hitboxCircle.cx} cy={o.hitboxCircle.cy} r={o.hitboxCircle.radius}
                          fill="rgba(78,197,241,0.15)" stroke="#ff7a6b" strokeWidth="0.35" strokeDasharray="1 0.6"
                          data-testid={`hit-shape-${o.learningObject?.canonicalName || o.id}`} />
                      );
                    }
                    if (shape === "polygon" && (o.hitboxPolygon || []).length >= 3) {
                      const pts = o.hitboxPolygon.map((p) => `${p[0]},${p[1]}`).join(" ");
                      return (
                        <polygon key={o.id} points={pts}
                          fill="rgba(78,197,241,0.15)" stroke="#ff7a6b" strokeWidth="0.35" strokeDasharray="1 0.6"
                          data-testid={`hit-shape-${o.learningObject?.canonicalName || o.id}`} />
                      );
                    }
                    return null;
                  })}
                </svg>
              )}
            </div>
          </TransformComponent>
        </TransformWrapper>
      )}
    </div>
  );
}
